import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { paymentApi } from '@/features/booking/api/payment.api';
import { verifyPaymentByOrderWithRetry, verifyPaymentWithRetry } from '@/features/booking/utils/verifyPaymentAfterCheckout';
import { Button } from '@/shared/components/ui';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';

function PaymentReturnContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [bookingId, setBookingId] = useState<string | null>(null);
  const verifyStarted = useRef(false);

  useEffect(() => {
    if (verifyStarted.current) return;
    verifyStarted.current = true;

    const verify = async () => {
      try {
        const paymentId = paymentApi.getStoredPaymentId();
        const providerOrderId =
          searchParams.get('order_id') ?? searchParams.get('orderId') ?? undefined;

        if (!paymentId && !providerOrderId) {
          setStatus('error');
          setMessage('Payment session expired. Please try again from your booking.');
          return;
        }

        const payment = paymentId
          ? await verifyPaymentWithRetry(paymentId)
          : await verifyPaymentByOrderWithRetry(providerOrderId!);

        if (payment.paymentStatus === 'SUCCESS') {
          setStatus('success');
          setBookingId(payment.bookingId);
        } else {
          setStatus('error');
          setMessage('Payment was not completed. Please try again.');
        }
      } catch (err) {
        setStatus('error');
        setMessage((err as Error).message ?? 'Payment verification failed');
      }
    };

    void verify();
  }, [searchParams]);

  useEffect(() => {
    if (status === 'success' && bookingId) {
      const timer = setTimeout(() => {
        navigate(`/bookings/${bookingId}/confirmation`, { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status, bookingId, navigate]);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress color="primary" sx={{ mb: 2 }} />
            <Typography variant="h6" color="textColors.heading">
              Confirming your payment…
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, mb: 2, color: 'success.main' }} />
            <Typography variant="h5" fontWeight={700} gutterBottom color="textColors.heading">
              Payment successful!
            </Typography>
            <Typography color="text.secondary">
              Redirecting to your booking confirmation…
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button component={RouterLink} to="/my-bookings" intent="primary-dark">
              My bookings
            </Button>
          </>
        )}
      </Container>
    </Box>
  );
}

export function PaymentReturnPage() {
  return (
    <ProtectedRoute>
      <PaymentReturnContent />
    </ProtectedRoute>
  );
}
